// src/screens/Moderation.js
//
// Drift / Community moderation queue.
//
// Reads user-filed reports (drift_reports) and lets an admin act on them.
// Everything runs through existing SECURITY DEFINER RPCs (guarded by
// _is_admin_user), so this screen only calls RPCs — no direct table access:
//   admin_drift_overview()                              → header stats
//   admin_drift_reports()                               → the queue (open first)
//   admin_drift_hide(target_type, id, reason)           → soft-hide content
//   admin_drift_set_member_status(user_id, status, r)   → suspend / reinstate
//   admin_drift_resolve_report(report_id, status, note) → dismissed | actioned
//
// A reported DM is the only way DM content ever reaches an admin; DMs cannot be
// hidden (private), so DM reports offer Suspend author / Dismiss only.

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import {
  C, FONT, Card, CardTitle, MetricCard, Grid, Btn, Pill, Input, Insight, Divider, EmptyState,
} from '../components/UI';

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setMob(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

const TARGET_LABEL = {
  message: 'Channel message',
  dm_message: 'Direct message',
  layover: 'Layover',
  layover_comment: 'Layover comment',
  member: 'Member',
};
const HIDEABLE = new Set(['message', 'layover', 'layover_comment']);

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function statusPill(status) {
  if (status === 'open') return <Pill type="amber">Open</Pill>;
  if (status === 'actioned') return <Pill type="green">Actioned</Pill>;
  if (status === 'dismissed') return <Pill type="dark">Dismissed</Pill>;
  return <Pill type="blue">{status}</Pill>;
}

export default function Moderation() {
  const mob = useIsMobile();
  const [overview, setOverview] = useState(null);
  const [reports, setReports] = useState(null); // null = loading, [] = loaded/empty
  const [selId, setSelId] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    const [ovRes, rpRes] = await Promise.all([
      supabase.rpc('admin_drift_overview'),
      supabase.rpc('admin_drift_reports'),
    ]);
    if (ovRes.error || rpRes.error) {
      setErr((ovRes.error || rpRes.error).message || 'Could not load moderation data.');
    } else {
      setErr(null);
    }
    if (ovRes.data) setOverview(ovRes.data);
    setReports(Array.isArray(rpRes.data) ? rpRes.data : []);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const sel = (reports || []).find((r) => r.id === selId) || null;
  const openReports = (reports || []).filter((r) => r.status === 'open');

  const runAction = useCallback(async (kind) => {
    if (!sel || busy) return;
    setBusy(true);
    setErr(null);
    const reason = note.trim() || null;
    try {
      if (kind === 'hide') {
        const { error } = await supabase.rpc('admin_drift_hide', {
          p_target_type: sel.target_type, p_id: sel.target_id, p_reason: reason,
        });
        if (error) throw error;
        const { error: e2 } = await supabase.rpc('admin_drift_resolve_report', {
          p_report_id: sel.id, p_status: 'actioned', p_note: reason || 'content hidden',
        });
        if (e2) throw e2;
      } else if (kind === 'suspend') {
        const { error } = await supabase.rpc('admin_drift_set_member_status', {
          p_user_id: sel.content_author_id, p_status: 'suspended', p_reason: reason,
        });
        if (error) throw error;
        const { error: e2 } = await supabase.rpc('admin_drift_resolve_report', {
          p_report_id: sel.id, p_status: 'actioned', p_note: reason || 'author suspended',
        });
        if (e2) throw e2;
      } else if (kind === 'dismiss') {
        const { error } = await supabase.rpc('admin_drift_resolve_report', {
          p_report_id: sel.id, p_status: 'dismissed', p_note: reason,
        });
        if (error) throw error;
      }
      setNote('');
      await load();
    } catch (e) {
      setErr(e.message || 'Action failed.');
    } finally {
      setBusy(false);
    }
  }, [sel, busy, note, load]);

  // ── Stat header ──────────────────────────────────────────────────────────
  const stats = overview && (
    <Grid cols={4}>
      <MetricCard label="Open reports" value={overview.reports_open ?? 0}
        accent={overview.reports_open > 0 ? C.red : C.green} />
      <MetricCard label="Members" value={overview.members_total ?? 0}
        sub={`${overview.members_active ?? 0} active · ${overview.members_suspended ?? 0} suspended`} />
      <MetricCard label="Messages · 24h" value={overview.messages_24h ?? 0}
        sub={`${overview.messages_total ?? 0} all time`} />
      <MetricCard label="Hidden content" value={overview.hidden_content ?? 0}
        accent={C.tealMid} />
    </Grid>
  );

  // ── Report list ──────────────────────────────────────────────────────────
  function ReportRow({ r }) {
    const active = r.id === selId;
    return (
      <div
        onClick={() => { setSelId(r.id); setNote(''); setErr(null); }}
        style={{
          padding: '11px 13px', borderRadius: 9, cursor: 'pointer', marginBottom: 7,
          background: active ? C.tealPale : C.white,
          border: `1px solid ${active ? C.tealMid : C.border}`,
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5, flexWrap: 'wrap' }}>
          <Pill type={r.target_type === 'dm_message' ? 'blue' : 'teal'}>
            {TARGET_LABEL[r.target_type] || r.target_type}
          </Pill>
          {statusPill(r.status)}
          <span style={{ marginLeft: 'auto', fontSize: '.7rem', color: C.textMuted }}>{timeAgo(r.created_at)}</span>
        </div>
        {r.reason && (
          <div style={{ fontSize: '.78rem', color: C.amberText, fontWeight: 600, marginBottom: 3 }}>
            {r.reason}
          </div>
        )}
        <div style={{
          fontSize: '.8rem', color: C.textMid, lineHeight: 1.4,
          overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>
          {r.content || <em style={{ color: C.textMuted }}>content removed or unavailable</em>}
        </div>
        <div style={{ fontSize: '.7rem', color: C.textMuted, marginTop: 5 }}>
          reported by {r.reporter || 'unknown'}
        </div>
      </div>
    );
  }

  const list = (
    <div>
      <CardTitle action={<Btn variant="outline" small onClick={load}>Refresh</Btn>}>
        Reports {reports ? `· ${openReports.length} open` : ''}
      </CardTitle>
      {reports === null && <div style={{ fontSize: '.82rem', color: C.textMuted, padding: '8px 2px' }}>Loading…</div>}
      {reports && reports.length === 0 && (
        <EmptyState
          icon={<svg width={34} height={34} viewBox="0 0 24 24" fill="none" stroke="#9aa5ab" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z"/><path d="M9 12l2 2 4-4"/></svg>}
          title="Nothing to moderate"
          body="No open reports. New reports from Drift members will appear here." />
      )}
      {reports && reports.map((r) => <ReportRow key={r.id} r={r} />)}
    </div>
  );

  // ── Detail / actions ─────────────────────────────────────────────────────
  function Detail() {
    if (!sel) {
      return <EmptyState icon={<svg width={34} height={34} viewBox="0 0 24 24" fill="none" stroke="#9aa5ab" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 8h6M9 12h6M9 16h4"/></svg>} title="Select a report" body="Choose a report to see the content and take action." />;
    }
    const open = sel.status === 'open';
    const canHide = HIDEABLE.has(sel.target_type);
    const canSuspend = !!sel.content_author_id;
    return (
      <div>
        {mob && (
          <Btn variant="outline" small onClick={() => setSelId(null)} style={{ marginBottom: 12 }}>← Back</Btn>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12, flexWrap: 'wrap' }}>
          <Pill type={sel.target_type === 'dm_message' ? 'blue' : 'teal'}>{TARGET_LABEL[sel.target_type] || sel.target_type}</Pill>
          {statusPill(sel.status)}
          <span style={{ fontSize: '.72rem', color: C.textMuted }}>{timeAgo(sel.created_at)}</span>
        </div>

        {sel.reason && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: '.68rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 3 }}>Reason</div>
            <div style={{ fontSize: '.85rem', color: C.amberText, fontWeight: 600 }}>{sel.reason}</div>
          </div>
        )}

        <div style={{ fontSize: '.68rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>Reported content</div>
        <div style={{
          background: C.bg, border: `1px solid ${C.border}`, borderRadius: 9,
          padding: '12px 14px', fontSize: '.88rem', color: C.text, lineHeight: 1.55,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 12,
        }}>
          {sel.content || <em style={{ color: C.textMuted }}>Content is gone (already hidden or deleted).</em>}
        </div>

        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: '.75rem', color: C.textMid, marginBottom: 14 }}>
          <span><strong style={{ color: C.text }}>Reporter:</strong> {sel.reporter || '—'}</span>
          <span><strong style={{ color: C.text }}>Author:</strong> {sel.content_author_id ? sel.content_author_id.slice(0, 8) + '…' : '—'}</span>
        </div>

        {open ? (
          <>
            <Divider />
            <div style={{ fontSize: '.68rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>
              Resolution note (optional)
            </div>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why you're taking this action…" style={{ marginBottom: 12 }} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {canHide && <Btn variant="primary" onClick={() => runAction('hide')}>Hide content</Btn>}
              {canSuspend && <Btn variant="danger" onClick={() => runAction('suspend')}>Suspend author</Btn>}
              <Btn variant="outline" onClick={() => runAction('dismiss')}>Dismiss</Btn>
            </div>
            {!canHide && sel.target_type === 'dm_message' && (
              <Insight type="info">Direct messages are private and can't be hidden — suspend the author or dismiss.</Insight>
            )}
            {busy && <div style={{ fontSize: '.78rem', color: C.textMuted, marginTop: 10 }}>Working…</div>}
          </>
        ) : (
          <Insight type={sel.status === 'actioned' ? 'ok' : 'info'}>
            {sel.status === 'actioned' ? 'Actioned' : 'Dismissed'}
            {sel.resolved_by ? ` by ${sel.resolved_by}` : ''}
            {sel.resolved_at ? ` · ${timeAgo(sel.resolved_at)}` : ''}
            {sel.resolution_note ? ` — “${sel.resolution_note}”` : ''}
          </Insight>
        )}
      </div>
    );
  }

  return (
    <div style={{ fontFamily: FONT }}>
      <div style={{ marginBottom: 6 }}>
        <h1 style={{ fontSize: '1.15rem', fontWeight: 600, color: C.text, margin: 0 }}>Community moderation</h1>
        <div style={{ fontSize: '.8rem', color: C.textMuted, marginTop: 3 }}>
          Reports filed by Drift members — hide content, suspend members, or dismiss.
        </div>
      </div>

      {stats}

      {err && <Insight type="warn">{err}</Insight>}

      {/* Desktop: list + detail side by side. Mobile: one or the other. */}
      {mob ? (
        <Card style={{ marginTop: 14 }}>{sel ? <Detail /> : list}</Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 14, marginTop: 14, alignItems: 'start' }}>
          <Card style={{ marginBottom: 0 }}>{list}</Card>
          <Card style={{ marginBottom: 0 }}><Detail /></Card>
        </div>
      )}
    </div>
  );
}
